import { Divider, Grid, IconButton, Tab, Tabs, Typography } from "@mui/material";
import { ChangeEvent, useContext, useEffect, useRef } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";

import styles from "../../../../assets/styles/componentStyles/templateLibraryStyles/modifyWhatsAppTemplate/importTemplatePopup.module.scss";

import GlobalButton from "../../../utility/globalButton/button";
import GlobalTextField from "../../../utility/globalTextfield";
import Required from "../../../utility/required";
import JsonTextEditor from "../../../utility/jsonTextEditor";
import { isInvalidExportFile, TEMPLATE_NAME_MAX_CHARS_MESSAGE, TEMPLATE_NAME_MAX_LIMIT } from "../constant";
import { UserContext, UserContextPayload } from "../../../../context/userContext";
import { useTemplateLibraryStore } from "../../../../store/templateLibraryStore";
import { useShallow } from "zustand/react/shallow";
import { createTemplateLibraryDraftPayload, createWhatsappTemplatePayload, createWhatsappTemplatePayloadForTemplateLibrary } from "../../../../models/axiosModel";
import { getTemplateCategoryId, TEMPLATE_CATEGORY, TEMPLATE_TYPES } from "../../channels/global/whatsapp/templates/utils";
import agent from "../../../../services/api";
import { useSearchParams } from "react-router-dom";
import { WhatsappTemplateContext, WhatsappTemplateContextPayload } from "../../../../context/whatsappTemplateContext";
import { useGenericStore } from "../../../../store/genericStore";

type ImportTemplatePopupProps = {
    title: string;
    handleClose: () => void;
    flagShowCreateTemplateButtons?: boolean;
    flagIsLegacyTemplate?: boolean;
}

const ImportTemplatePopup = (props: ImportTemplatePopupProps) => {

    const { title, handleClose, flagShowCreateTemplateButtons = false, flagIsLegacyTemplate = false } = props;
    
    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;

    const { getTemplateList, setImportLegacyTemplatePayload, importLegacyTemplateName, 
        setImportLegacyTemplateName, importLegacyTemplateJson, setImportLegacyTemplateJson, importLegacyTemplateFile, 
        setImportLegacyTemplateFile, importLegacyTemplateFileName, setImportLegacyTemplateFileName,  importLegacyTemplateSelectedTab, 
        setImportLegacyTemplateSelectedTab } = useContext(WhatsappTemplateContext) as WhatsappTemplateContextPayload;

    const { setHasUnsavedChanges, createDraftTemplate, templateLibraryCategoryList, setImportTemplatePayload, importTemplateName, setImportTemplateName, importTemplateJson, setImportTemplateJson, 
        setImportTemplateFile, importTemplateFile, importTemplateFileName, setImportTemplateFileName, importTemplateSelectedTab, 
        setImportTemplateSelectedTab, getTemplateLibraryDraftListApiCall
    } = useTemplateLibraryStore(useShallow((store) => (
        {
            setHasUnsavedChanges: store.setHasUnsavedChanges,
            createDraftTemplate: store.createDraftTemplate,
            templateLibraryCategoryList: store.templateLibraryCategoryList,
            setImportTemplatePayload: store.setImportTemplatePayload,
            importTemplateName: store.importTemplateName,
            setImportTemplateName: store.setImportTemplateName,
            importTemplateJson: store.importTemplateJson,
            setImportTemplateJson: store.setImportTemplateJson,
            importTemplateFile: store.importTemplateFile,
            setImportTemplateFile: store.setImportTemplateFile,
            importTemplateFileName: store.importTemplateFileName,
            setImportTemplateFileName: store.setImportTemplateFileName,
            importTemplateSelectedTab: store.importTemplateSelectedTab,
            setImportTemplateSelectedTab: store.setImportTemplateSelectedTab,
            getTemplateLibraryDraftListApiCall: store.getTemplateLibraryDraftListApiCall,
        }
    )))

    const { showInfo, showSuccess, showError } = useGenericStore(useShallow((state) => ({
        showInfo: state.showInfo,
        showSuccess: state.showSuccess,
        showError: state.showError
    })))

    const [searchParams] = useSearchParams();
    const id = searchParams.get('id');

    const uploadFileInputRef = useRef<HTMLInputElement | null>(null);

    const activeSelectedTab = flagIsLegacyTemplate ? importLegacyTemplateSelectedTab : importTemplateSelectedTab;
    const activeTemplateName = flagIsLegacyTemplate ? importLegacyTemplateName : importTemplateName;
    const activeTemplateJson = flagIsLegacyTemplate ? importLegacyTemplateJson : importTemplateJson;
    const activeTemplateFileName = flagIsLegacyTemplate ? importLegacyTemplateFileName : importTemplateFileName;

    const handleTabsChange = (event: React.SyntheticEvent | React.MouseEvent | React.KeyboardEvent, newValue: number) => {
        event.preventDefault();
        event.stopPropagation();

        if(flagIsLegacyTemplate) {
            setImportLegacyTemplateSelectedTab(newValue);
        } else {
            setImportTemplateSelectedTab(newValue);
        }
    };

    const handleOpenFileManager = () => {
        uploadFileInputRef.current?.click();
    };

    const handleUploadedFile = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        if (selectedFile) {
            if(flagIsLegacyTemplate) {
                setImportLegacyTemplateFileName(selectedFile.name);
                setImportLegacyTemplateFile(selectedFile);
            } else {
                setImportTemplateFileName(selectedFile.name);
                setImportTemplateFile(selectedFile);
            }
        }
    };

    const handleRemoveUploadedFile = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if(flagIsLegacyTemplate) {
            setImportLegacyTemplateFileName(null);
            setImportLegacyTemplateFile(null);
        } else {
            setImportTemplateFileName(null);
            setImportTemplateFile(null);
        }

        if (uploadFileInputRef.current) {
            uploadFileInputRef.current.value = "";
        }
    };

    const isImportTemplateButtonDisabled = (selectedTab: number) => {
        const isTemplateNameInvalid = !activeTemplateName?.trim() || (activeTemplateName?.trim()?.length > TEMPLATE_NAME_MAX_LIMIT);
        const isTemplateJsonInvalid = selectedTab === 0 ? (!activeTemplateFileName?.trim()) : (!activeTemplateJson?.trim());
        
        if(flagShowCreateTemplateButtons) {
            return isTemplateNameInvalid || isTemplateJsonInvalid;
        } else {
            return isTemplateJsonInvalid;
        }
    }

    const resetImportTemplateState = () => {
        if(flagIsLegacyTemplate) {
            setImportLegacyTemplatePayload(null);
            setImportLegacyTemplateName(null);
            setImportLegacyTemplateJson(null);
            setImportLegacyTemplateFile(null);
            setImportLegacyTemplateFileName(null);
            setImportLegacyTemplateSelectedTab(0);
        }
        else {
            setImportTemplatePayload(null);
            setImportTemplateName(null);
            setImportTemplateJson(null);
            setImportTemplateFile(null);
            setImportTemplateFileName(null);
            setImportTemplateSelectedTab(0);
        }
    }

    const readFileAsText = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
            reader.onerror = () => reject(new Error("Read file failed"));
            reader.readAsText(file, "UTF-8");
        });

    const loadTemplatesData = async (metaIntegrationWhatsappId?: number) => {
        const templateList = (metaIntegrationWhatsappId && metaIntegrationWhatsappId > 0)
            ? getTemplateList(metaIntegrationWhatsappId)
            : Promise.resolve(null);

        const [draftRes, templatesRes] = await Promise.all([
            getTemplateLibraryDraftListApiCall(),
            templateList,
        ]);

        if (!(draftRes?.status)) {
            handleError(draftRes);
        }
        if (templatesRes && !(templatesRes?.status)) {
            handleError(templatesRes);
        }
    }

    // Create Template
    const handleCreateTemplate = async () => {
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }))
        try {
            const rawPayload = importTemplateSelectedTab === 0
                ? JSON.parse(await readFileAsText(importTemplateFile ?? new File([], "")))
                : JSON.parse(importTemplateJson ?? "");

            if(!rawPayload) {
                showError("Invalid template payload.")
                return;
            }
            if (isInvalidExportFile(rawPayload)) {
                showError("Invalid file")
                return;
            }
            if(rawPayload?.whatsapp_template_flag_flow) {
                handleClose();
                showError("WhatsApp templates having Flows are not supported.")
                return;
            }

            const templateData = Array.isArray(rawPayload?.whatsapp_template_data)
                ? rawPayload.whatsapp_template_data?.[0]
                : rawPayload?.whatsapp_template_data;

            if (!templateData) {
                showError("Invalid template payload.")
                return;
            }

            const carouselCards = (Array.isArray(rawPayload?.whatsapp_template_data)
                ? rawPayload.whatsapp_template_data
                : (templateData?.whatsapp_template_carousel?.whatsapp_template_carousel_card ?? [])
            ).map((card: any) => ({
                whatsapp_template_header: card?.whatsapp_template_header,
                whatsapp_template_body: card?.whatsapp_template_body,
                whatsapp_template_button: card?.whatsapp_template_button ?? [],
                whatsapp_template_footer: card?.whatsapp_template_footer,
            }));

            const isCarouselTemplate = rawPayload?.whatsapp_template_type?.name?.toLowerCase() === TEMPLATE_TYPES.CAROUSEL?.toLowerCase();
            const isAuthenticationTemplate = Number(templateData?.whatsapp_template_category_id) === Number(getTemplateCategoryId(TEMPLATE_CATEGORY?.AUTHENTICATION, templateLibraryCategoryList ?? []));

            const payload: createWhatsappTemplatePayloadForTemplateLibrary = {
                whatsapp_template_id: null,
                meta_integration_whatsapp_id: Number(id ?? 0),
                whatsapp_template_flag_interactive_message: Boolean(rawPayload?.whatsapp_template_flag_interactive_message),
                whatsapp_template_flag_flow: Boolean(rawPayload?.whatsapp_template_flag_flow),
                interactive_type_id: rawPayload?.interactive_type?.interactive_type_id ?? rawPayload?.interactive_type_id ?? null,
                interactive_list_button_text: rawPayload?.interactive_list_button_text ?? null,
                flag_named_parameter: Boolean(rawPayload?.flag_named_parameter),
                whatsapp_template_type_id: rawPayload?.whatsapp_template_type?.whatsapp_template_type_id ?? rawPayload?.whatsapp_template_type_id ?? 0,
                whatsapp_template_data: {
                    whatsapp_template_data_id: templateData?.whatsapp_template_data_id ?? 0,
                    whatsapp_template_category_id: templateData?.whatsapp_template_category_id ?? getTemplateCategoryId(TEMPLATE_CATEGORY?.MARKETING, templateLibraryCategoryList ?? []),
                    whatsapp_language_id: templateData?.whatsapp_language_data?.whatsapp_language_id ?? templateData?.whatsapp_language_id ?? 0,
                    whatsapp_template_name: "",
                    whatsapp_template_display_name: importTemplateName?.trim() ?? "",
                    whatsapp_template_description: templateData?.whatsapp_template_description ?? "",
                    whatsapp_template_header: isCarouselTemplate ? {} as any : templateData?.whatsapp_template_header,
                    whatsapp_template_body: isCarouselTemplate ? {} as any : (templateData?.whatsapp_template_body ?? {
                        whatsapp_template_body_id: "",
                        whatsapp_template_body_value: "",
                        whatsapp_template_body_description: "",
                        whatsapp_template_body_variable_data: [],
                    }),
                    whatsapp_template_button: isCarouselTemplate ? [] : (Array.isArray(templateData?.whatsapp_template_button) ? templateData.whatsapp_template_button : []),
                    whatsapp_template_footer: isCarouselTemplate ? {} as any : templateData?.whatsapp_template_footer,
                    whatsapp_template_carousel: {
                        whatsapp_template_carousel_header: rawPayload?.whtatsapp_carousel_header ?? "",
                        whatsapp_template_carousel_header_dynamic_variable: rawPayload?.whtatsapp_carousel_header_variable_data ?? [],
                        whatsapp_template_carousel_card: carouselCards ?? []
                    },
                    whatsapp_template_security_recommendation: templateData?.whatsapp_template_security_recommendation ?? false,
                    whatsapp_template_code_expiration_minutes: templateData?.whatsapp_template_code_expiration_minutes ?? null,
                },
            };

            // Remove header for Authentication template
            if(isAuthenticationTemplate) {
                delete (payload?.whatsapp_template_data as any)?.whatsapp_template_header;
            }

            const response = await agent.createWhatsappTemplate.post(payload);

            if (response.status) {
                handleClose();
                showSuccess("Template Created Successfully!")
                setHasUnsavedChanges(false);
                const metaIntegrationWhatsappId = Number(response?.data?.[0]?.meta_integration_whatsapp_id ?? payload?.meta_integration_whatsapp_id ?? id ?? 0);
                await loadTemplatesData(metaIntegrationWhatsappId);
            } else {
                handleError(response);
            }

        } catch(error) {
            showError("Error creating template.")
        } finally {
            handleClose();
            resetImportTemplateState();
            setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
        }
    }

    // Save as Draft
    const handleSaveAsDraft = async () => {
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }))
        try {
            let payload = importTemplateSelectedTab === 0 
                            ? JSON.parse(await readFileAsText(importTemplateFile ?? new File([], "")))
                            : JSON.parse(importTemplateJson ?? "");
            
            const templateData = Array.isArray(payload.whatsapp_template_data) ? payload.whatsapp_template_data[0] : payload.whatsapp_template_data;
            if (!templateData) {
                showError("Invalid template payload.")
                return;
            }
            if (isInvalidExportFile(payload)) {
                showError("Invalid file")
                return;
            }

            payload.meta_integration_whatsapp_id = 0;
            templateData.whatsapp_template_display_name = importTemplateName?.trim() ?? "";
            templateData.whatsapp_template_name = "";
            const categoryId = templateData?.whatsapp_template_category_id ?? getTemplateCategoryId(TEMPLATE_CATEGORY?.MARKETING, templateLibraryCategoryList ?? []);
            
            const payloadToCreateDraft = {
                whatsapp_template_category_id: categoryId,
                json_data: JSON.stringify(payload),
            }

            const response = await createDraftTemplate(payloadToCreateDraft as unknown as createTemplateLibraryDraftPayload);

            if (response.status) {
                handleClose();
                showSuccess("Draft Saved Successfully!")
                setHasUnsavedChanges(false);
                const metaIntegrationWhatsappId = Number(id ?? 0);
                await loadTemplatesData(metaIntegrationWhatsappId);
            } else {
                handleError(response);
            }
        } 
        catch {
            showError("Error saving draft.")
        } 
        finally {
            handleClose();
            resetImportTemplateState();
            setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
        }
    }

    // Import Template
    const handleImportTemplate = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        try {
            let jsonContent = "";
            if (activeSelectedTab === 0) {
                const file = uploadFileInputRef.current?.files?.[0] 
                    ?? (flagIsLegacyTemplate ? importLegacyTemplateFile : importTemplateFile) 
                    ?? null;

                if (!file) {
                    showError("Please upload a JSON file.")
                    return;
                }
                jsonContent = (await readFileAsText(file)).trim();
                if (!jsonContent) {
                    showError("File is empty.")
                    return;
                }
            } else {
                jsonContent = activeTemplateJson?.trim() || "";
                if (!jsonContent) {
                    showError("JSON content is required.")
                    return;
                }
            }
            const parsed = JSON.parse(jsonContent);

            // const templateTypeName = parsed?.whatsapp_template_type?.name?.toLowerCase();
            // const isCarousel = (templateTypeName === TEMPLATE_TYPES['CAROUSEL']?.toLowerCase());
            // const isInteractive = parsed?.whatsapp_template_flag_interactive_message;

            // if(!flagIsLegacyTemplate && (isInteractive || isCarousel)){
            //     handleError({ flagShowError: true, message: "Interactive or Carousel templates are not supported." });
            //     handleClose();
            //     return;
            // }

            if(flagIsLegacyTemplate) {
                setImportLegacyTemplatePayload(parsed as createWhatsappTemplatePayload);
            } 
            else {
                setImportTemplatePayload(parsed as createWhatsappTemplatePayloadForTemplateLibrary);
            }

            handleClose();
        } catch {
            showError("Invalid JSON")
        } finally {
            setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
        }
    };

    
    useEffect(() => {
        return () => {
            resetImportTemplateState();
        };
    }, []);

    return (

        <Grid container className={styles.importTemplatePopup}>
            
            {/* Title */}
            <Grid item className={styles.popupTitleWrap}>
                <span className={styles.popupTitleStyle}>
                    {title}
                </span>
            </Grid>

            {/* Divider */}
            <Grid item>
                <Divider className={styles.popupDividerStyle} />
            </Grid>

            {/* Content */}
            <Grid item>
                <Grid container className={styles.popupContentContainer}>
                    {/* Tabs */}
                    <Tabs 
                        value={activeSelectedTab} 
                        onChange={(event, newValue) => handleTabsChange(event, newValue)} 
                        className={styles.tabsContainer}
                    >
                        <Tab label="Upload JSON File" className={styles.tabsItem} />
                        <Tab label="Insert JSON" className={styles.tabsItem} />
                    </Tabs>

                    {/* Template Name */}
                    {flagShowCreateTemplateButtons && (
                        <Grid item className={styles.textFieldItem}>
                            <span>
                                Template Name <Required />
                            </span>
                            <GlobalTextField 
                                fullWidth
                                placeholder="Enter Template Name"
                                value={importTemplateName ?? ""}
                                onChange={(e) => {
                                    if (e.target.value?.length > TEMPLATE_NAME_MAX_LIMIT) {
                                        showInfo(TEMPLATE_NAME_MAX_CHARS_MESSAGE);
                                        return;
                                    } else {
                                        if(flagIsLegacyTemplate) {
                                            setImportLegacyTemplateName(e.target.value.slice(0, TEMPLATE_NAME_MAX_LIMIT));
                                        } else {
                                            setImportTemplateName(e.target.value.slice(0, TEMPLATE_NAME_MAX_LIMIT));
                                        }
                                    }
                                }}
                                className={styles.textField}
                            />
                        </Grid>
                    )}

                    {/* Tab Panels */}

                    {/* Upload JSON File */}
                    {activeSelectedTab === 0 && (
                        <Grid container className={styles.uploadFileBoxContainer}>
                            <Grid item className={styles.uploadFileBoxContainer}>
                                <Grid item className={styles.textFieldItem}>
                                    <span>
                                        Upload JSON File <Required />
                                    </span>
                                    <Grid
                                        item
                                        onClick={handleOpenFileManager}
                                        className={styles.uploadFileBox}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                handleOpenFileManager();
                                            }
                                        }}
                                    >
                                        {activeTemplateFileName && (
                                            <IconButton
                                                className={styles.removeUploadedFileButton}
                                                onClick={handleRemoveUploadedFile}
                                                aria-label="Remove uploaded file"
                                            >
                                                <CloseRoundedIcon className={styles.removeUploadedFileIcon} />
                                            </IconButton>
                                        )}
                                        <input
                                            ref={uploadFileInputRef}
                                            id="import-template-json-file"
                                            type="file"
                                            accept=".json,application/json"
                                            onChange={handleUploadedFile}
                                            className={styles.uploadFileInput}
                                        />
                                        <UploadFileRoundedIcon className={styles.uploadFileIcon} />
                                        <Typography className={styles.uploadFileText}>
                                            {activeTemplateFileName || "Upload a JSON file"}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    {/* Insert JSON */}
                    {activeSelectedTab === 1 && (
                        <Grid container className={styles.textFieldContainer}>
                            <Grid item className={styles.textFieldItem}>
                                <span>
                                    JSON Content <Required />
                                </span>
                                <JsonTextEditor
                                    value={activeTemplateJson ?? ""}
                                    onChange={(newValue) => {
                                        if(flagIsLegacyTemplate) {
                                            setImportLegacyTemplateJson(newValue);
                                        } else {
                                            setImportTemplateJson(newValue);
                                        }
                                    }}
                                    placeholder="Write your JSON content here"
                                />
                            </Grid>
                        </Grid>
                    )}

                </Grid>
            </Grid>

            {/* Buttons */}
            <Grid item>
                <Grid container className={styles.buttonsContainer}>
                    {flagShowCreateTemplateButtons ? (
                        <Grid container className={styles.buttonsContent}>
                            <Grid item className={styles.buttonItem}>
                                <GlobalButton 
                                    buttonText={"Save as Draft"} 
                                    onClick={() => handleSaveAsDraft()} 
                                    disabled={isImportTemplateButtonDisabled(activeSelectedTab)}
                                    className='borderedButtonStyle' 
                                />
                            </Grid>
                            <Grid item className={styles.buttonItem}>
                                <GlobalButton
                                    buttonText={"Create Template"}
                                    onClick={() => handleCreateTemplate()}
                                    disabled={isImportTemplateButtonDisabled(activeSelectedTab)}
                                    className='primaryButtonStyle'
                                />
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container className={styles.buttonsContent}>
                            <Grid item className={styles.buttonItem}>
                                <GlobalButton 
                                    buttonText="Import Template"
                                    onClick={() => handleImportTemplate()}
                                    disabled={isImportTemplateButtonDisabled(activeSelectedTab)}
                                    className="primaryButtonStyle" 
                                />
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </Grid>
    )
}

export default ImportTemplatePopup;